import { useMemo, useState } from 'react';
import { NavLink, Outlet, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { api } from '../lib/api';
import { Button } from './Button';
import { IconDashboard, IconPencil, IconInbox, IconTrophy, IconGear, IconUser } from './icons';
import { cn } from '../lib/cn';
import type { AppConfig } from '../lib/types';

const baseNav = [
  { to: '/dashboard', label: 'Dashboard', Icon: IconDashboard, end: true },
  { to: '/questions', label: 'Questions', Icon: IconPencil },
  { to: '/submissions', label: 'Submissions', Icon: IconInbox },
  { to: '/board', label: 'Leaderboard', Icon: IconTrophy },
  { to: '/settings', label: 'Settings', Icon: IconGear },
];

/** Wordmark used in the panel rail and on public pages. */
export function Wordmark({ subtitle }: { subtitle?: string }) {
  return (
    <Link to="/dashboard" className="group block no-underline">
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] bg-accent text-accent-contrast shadow-[var(--shadow-card)]"
          aria-hidden="true"
        >
          <span className="font-display text-lg" style={{ fontVariationSettings: "'opsz' 30" }}>
            A
          </span>
        </span>
        <span className="leading-tight">
          <span
            className="block font-display text-[1.05rem] text-ink"
            style={{ fontVariationSettings: "'opsz' 40", fontWeight: 600 }}
          >
            Aptitude
          </span>
          <span className="eyebrow block">{subtitle ?? 'Daily Quiz'}</span>
        </span>
      </div>
    </Link>
  );
}

export function PanelLayout() {
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  // Fetch the panel config once to learn if the signed-in user is a super
  // admin. The /admin route is rendered for non-supers too (the server gates
  // the API), but hiding the nav item keeps the panel honest.
  const cfg = useApi<AppConfig>(() => api.getConfig(), []);
  const isSuperAdmin = useMemo(() => {
    const supers = (cfg.data?.superAdminEmails ?? []).map((e) => e.toLowerCase());
    const me = (user?.email ?? '').toLowerCase();
    return Boolean(me && supers.includes(me));
  }, [cfg.data, user]);
  const nav = useMemo(
    () =>
      isSuperAdmin
        ? [...baseNav, { to: '/admin', label: 'Admin', Icon: IconUser, end: undefined }]
        : baseNav,
    [isSuperAdmin],
  );

  const navItems = (
    <nav aria-label="Primary" className="flex flex-col gap-1">
      {nav.map(({ to, label, Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium no-underline transition-colors',
              isActive
                ? 'bg-accent-soft text-accent shadow-[var(--shadow-inset)]'
                : 'text-ink-soft hover:bg-paper-sunken hover:text-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon className={cn('text-[1.15rem]', isActive ? 'text-accent' : 'text-ink-faint')} />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="mx-auto flex min-h-full max-w-[1400px] gap-6 px-4 py-6 lg:px-8">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      {/* Rail */}
      <aside className="sticky top-6 hidden h-[calc(100vh-3rem)] w-64 shrink-0 flex-col rounded-[var(--radius-lg)] border border-edge bg-paper-raised p-5 shadow-[var(--shadow-card)] lg:flex">
        <Wordmark />
        <div className="my-6 h-px bg-edge" />
        {navItems}
        <div className="mt-auto">
          <div className="mb-3 h-px bg-edge" />
          <div className="flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-edge bg-paper-sunken text-sm font-semibold text-ink-soft">
              {user?.photoURL ? (
                <img src={user.photoURL} alt="" className="h-full w-full object-cover" />
              ) : (
                (user?.displayName?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {user?.displayName ?? 'Teacher'}
              </p>
              <p className="truncate text-xs text-ink-faint">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            block
            className="mt-3 justify-start"
            loading={signingOut}
            onClick={async () => {
              setSigningOut(true);
              try {
                await signOut();
              } finally {
                setSigningOut(false);
              }
            }}
          >
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="mb-5 flex items-center justify-between lg:hidden">
          <Wordmark />
          <Button variant="ghost" size="sm" onClick={() => signOut()}>
            Sign out
          </Button>
        </header>
        <main id="main" className="min-w-0 flex-1">
          <Outlet />
        </main>
        {/* Mobile bottom nav */}
        <nav
          aria-label="Primary"
          className="sticky bottom-0 z-30 mt-6 flex items-center justify-around rounded-[var(--radius-lg)] border border-edge bg-paper-raised/95 p-1.5 shadow-[var(--shadow-raised)] backdrop-blur lg:hidden"
        >
          {nav.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center gap-0.5 rounded-[var(--radius-md)] px-1 py-1.5 text-[0.65rem] font-medium no-underline',
                  isActive ? 'text-accent' : 'text-ink-faint',
                )
              }
            >
              <Icon className="text-lg" />
              <span>{label.split(' ')[0]}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
